import React, { useState, useMemo, useEffect } from 'react';
import { 
  Form, Input, Button, Card, Row, Col, Typography, Alert, 
  Badge, InputNumber, Radio, Statistic, Divider, Select, Space,
  Tooltip, Collapse, FormInstance
} from 'antd';
import {
  CreditCardOutlined,
  DollarOutlined,
  GiftOutlined,
  StarOutlined,
  RocketOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  LockOutlined,
  TagOutlined,
  CalculatorOutlined,
  InfoCircleOutlined,
  PercentageOutlined,
  TrophyOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { PricingConfig } from './pricingConfig';
import { showNotification } from './registrationHelpers';
import couponService from '../../../../../services/coupon/coupon.service';
import { Product } from '../../../../../services/product/product.service';
import './register.css'
import BillingAddressForm from '../../../../shared/components/BillingAddressForm';
import referralService from '../../../../../services/referral/referral.service';
import { ReferralRewardInfo, RewardType } from '../../../../../types/referral.types';
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface PricingFormProps {
  form: FormInstance<any>;
  onFinish: (values: any) => Promise<void>;
  loading: boolean;
  onPrevious: () => void;
  userCount: number;
  setUserCount: (count: number) => void;
  selectedPlan: 'trial' | 'paid';
  setSelectedPlan: (plan: 'trial' | 'paid') => void;
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
  products: Product[];
  calculatePricing: any;
  appliedCoupon: any;
  setAppliedCoupon: (coupon: any) => void;
  user: any;
  billingAddress?: any;
  setBillingAddress?: (address: any) => void;
  companyData?: any;
}

const PricingForm: React.FC<PricingFormProps> = ({
  form,
  onFinish,
  loading,
  onPrevious,
  userCount,
  setUserCount,
  selectedPlan,
  setSelectedPlan,
  selectedProduct,
  setSelectedProduct,
  products,
  calculatePricing,
  appliedCoupon,
  setAppliedCoupon,
  user,
  billingAddress,
  setBillingAddress,
  companyData
}) => {
  // Yeni state'ler
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [isBillingAddressValid, setIsBillingAddressValid] = useState(false);

  // Referans kodu state'leri
  const [referralCode, setReferralCode] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralRewardInfo, setReferralRewardInfo] = useState<ReferralRewardInfo | null>(null);

  // Component mount olduğunda localStorage'dan referans kodunu oku ve otomatik uygula
  useEffect(() => {
    const pendingReferralData = localStorage.getItem('pendingReferralCode');
    
    // Debug için console.log
    console.log('🔍 Referans kodu kontrolü (Component mount):', {
      pendingReferralData,
      selectedProduct: selectedProduct?.id,
      hasRewardInfo: !!referralRewardInfo
    });
    
    if (!pendingReferralData || referralRewardInfo) {
      return; // Kod yok veya zaten uygulanmış
    }
    
    try {
      // JSON formatında kaydedilmişse parse et
      let referralCode: string;
      let rewardInfo: ReferralRewardInfo | null = null;
      
      try {
        const parsed = JSON.parse(pendingReferralData);
        if (parsed.code && parsed.rewardInfo) {
          // JSON formatında kaydedilmiş (CompanyForm'dan geldi)
          referralCode = parsed.code;
          rewardInfo = parsed.rewardInfo;
          if (rewardInfo) {
            console.log('✅ Referans kodu bilgileri localStorage\'dan okundu (JSON format):', {
              code: referralCode,
              rewardType: rewardInfo.rewardType,
              rewardValue: rewardInfo.rewardValue
            });
          }
        } else {
          // Eski format (sadece kod string)
          referralCode = parsed;
        }
      } catch {
        // String formatında kaydedilmiş (eski format veya hata durumu)
        referralCode = pendingReferralData.trim().toUpperCase();
      }
      
      // Kod ve rewardInfo varsa direkt uygula
      if (referralCode && rewardInfo) {
        setReferralCode(referralCode);
        setReferralRewardInfo(rewardInfo);
        console.log('✅ Referans kodu bilgileri uygulandı:', {
          code: referralCode,
          rewardType: rewardInfo.rewardType,
          rewardValue: rewardInfo.rewardValue,
          description: rewardInfo.description
        });
      } else if (referralCode && selectedProduct) {
        // Sadece kod varsa ve paket seçilmişse API'den doğrula
        setReferralCode(referralCode);
        const autoApplyReferralCode = async () => {
          try {
            setReferralLoading(true);
            console.log('🔄 Referans kodu API\'den doğrulanıyor:', referralCode);
            
            const apiRewardInfo = await referralService.getRewardInfo(referralCode);
            
            if (apiRewardInfo) {
              setReferralRewardInfo(apiRewardInfo);
              // Bilgileri localStorage'a kaydet
              localStorage.setItem('pendingReferralCode', JSON.stringify({
                code: referralCode,
                rewardInfo: apiRewardInfo
              }));
              console.log('✅ Referans kodu API\'den doğrulandı ve uygulandı');
            } else {
              localStorage.removeItem('pendingReferralCode');
              setReferralCode('');
            }
          } catch (error) {
            console.error('❌ Referans kodu doğrulama hatası:', error);
            localStorage.removeItem('pendingReferralCode');
            setReferralCode('');
          } finally {
            setReferralLoading(false);
          }
        };
        
        autoApplyReferralCode();
      } else if (referralCode) {
        // Sadece kod varsa state'e set et, paket seçilince uygulanacak
        setReferralCode(referralCode);
      }
    } catch (error) {
      console.error('❌ Referans kodu parse hatası:', error);
      localStorage.removeItem('pendingReferralCode');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sadece component mount olduğunda çalışsın

  // selectedProduct seçildiğinde referans kodunu tekrar kontrol et ve uygula
  useEffect(() => {
    if (!selectedProduct || referralRewardInfo) {
      return; // Paket seçilmemiş veya kod zaten uygulanmış
    }
    
    const pendingReferralData = localStorage.getItem('pendingReferralCode');
    
    if (!pendingReferralData) {
      return;
    }
    
    try {
      let referralCode: string;
      let rewardInfo: ReferralRewardInfo | null = null;
      
      try {
        const parsed = JSON.parse(pendingReferralData);
        if (parsed.code && parsed.rewardInfo) {
          referralCode = parsed.code;
          rewardInfo = parsed.rewardInfo;
        } else {
          referralCode = parsed;
        }
      } catch {
        referralCode = pendingReferralData.trim().toUpperCase();
      }
      
      // Eğer rewardInfo varsa direkt uygula
      if (rewardInfo) {
        setReferralCode(referralCode);
        setReferralRewardInfo(rewardInfo);
        console.log('✅ Referans kodu paket seçildikten sonra uygulandı (localStorage\'dan):', {
          code: referralCode,
          rewardType: rewardInfo.rewardType,
          rewardValue: rewardInfo.rewardValue
        });
      } else if (referralCode) {
        // Sadece kod varsa API'den doğrula
        console.log('🔄 Paket seçildi, referans kodu API\'den doğrulanıyor:', referralCode);
        
        const autoApplyReferralCode = async () => {
          try {
            setReferralLoading(true);
            setReferralCode(referralCode);
            
            const apiRewardInfo = await referralService.getRewardInfo(referralCode);
            
            if (apiRewardInfo) {
              setReferralRewardInfo(apiRewardInfo);
              // Bilgileri localStorage'a kaydet
              localStorage.setItem('pendingReferralCode', JSON.stringify({
                code: referralCode,
                rewardInfo: apiRewardInfo
              }));
              console.log('✅ Referans kodu paket seçildikten sonra uygulandı (API\'den):', {
                rewardType: apiRewardInfo.rewardType,
                rewardValue: apiRewardInfo.rewardValue
              });
            } else {
              localStorage.removeItem('pendingReferralCode');
              setReferralCode('');
            }
          } catch (error) {
            console.error('❌ Referans kodu uygulama hatası:', error);
            localStorage.removeItem('pendingReferralCode');
            setReferralCode('');
          } finally {
            setReferralLoading(false);
          }
        };
        
        autoApplyReferralCode();
      }
    } catch (error) {
      console.error('❌ Referans kodu parse hatası:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]); // selectedProduct seçildiğinde çalışsın



  // Kupon uygulama fonksiyonu
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showNotification('warning', 'Uyarı', 'Lütfen bir kupon kodu girin');
      return;
    }

    if (!selectedProduct) {
      showNotification('warning', 'Uyarı', 'Lütfen önce bir paket seçin');
      return;
    }

    setCouponLoading(true);
    
    try {
      // Backend API çağrısı - Base price kullan (kupon öncesi fiyat)
      const currentBasePrice = userCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;

      const response = await couponService.validateCoupon({
        code: couponCode.trim().toUpperCase(),
        orderAmount: currentBasePrice,
        companyId: user?.companyId,
        productId: selectedProduct.id,
        userCount: userCount
      });
      
      if (response.isValid) {
        // Kupon geçerli - fiyata uygula
        const appliedCouponData = {
          isValid: true,
          code: couponCode.trim().toUpperCase(),
          originalAmount: currentBasePrice,
          finalAmount: response.finalAmount || currentBasePrice,
          savings: response.discountAmount || 0,
          message: response.message,
          couponType: response.couponType,
          couponValue: response.couponValue,
          // Kupon kısıtlamaları
          lockedProductId: selectedProduct.id,
          lockedUserCount: userCount
        };
        
        setAppliedCoupon(appliedCouponData);
        
        // Kupon tipine göre mesaj oluştur
        const discountText = response.couponType === 'percentage' 
          ? `%${response.couponValue} indirim`
          : `₺${response.couponValue} indirim`;
        
        showNotification('success', 'Kupon Uygulandı! 🎉', 
          `${response.message} - ${discountText} uygulandı! ₺${(response.discountAmount || 0).toFixed(2)} tasarruf!`);
            
      } else {
        // Kupon geçersiz
        showNotification('error', 'Kupon Hatası', response.message || 'Geçersiz kupon kodu');
      }
      
    } catch (error: any) {
      // console.error('Kupon doğrulama hatası:', error);
      const errorMessage = error.response?.data?.message || 'Kupon doğrulanırken bir hata oluştu. Lütfen tekrar deneyin.';
      showNotification('error', 'Bağlantı Hatası', errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  // Kupon kaldırma
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    showNotification('info', 'Kupon Kaldırıldı', 'Kupon indirimi kaldırıldı');
  };

  // Referans kodu doğrulama fonksiyonu
  const handleApplyReferralCode = async () => {
    if (!referralCode.trim()) {
      showNotification('warning', 'Uyarı', 'Lütfen bir referans kodu girin');
      return;
    }

    setReferralLoading(true);
    
    try {
      const rewardInfo = await referralService.getRewardInfo(referralCode.trim().toUpperCase());
      
      if (rewardInfo) {
        setReferralRewardInfo(rewardInfo);
        showNotification('success', 'Referans Kodu Geçerli! 🎉', rewardInfo.description);
      } else {
        setReferralRewardInfo(null);
        showNotification('error', 'Geçersiz Kod', 'Girdiğiniz referans kodu geçersiz veya süresi dolmuş.');
      }
    } catch (error: any) {
      console.error('Referans kodu doğrulama hatası:', error);
      showNotification('error', 'Bağlantı Hatası', 'Referans kodu doğrulanırken bir hata oluştu. Lütfen tekrar deneyin.');
      setReferralRewardInfo(null);
    } finally {
      setReferralLoading(false);
    }
  };

  // Referans kodunu kaldırma
  const handleRemoveReferralCode = () => {
    setReferralRewardInfo(null);
    setReferralCode('');
    showNotification('info', 'Referans Kodu Kaldırıldı', 'Referans kodu kaldırıldı');
  };

  // Referans kodundan indirim hesaplama
  const calculateReferralDiscount = (baseAmount: number): number => {
    if (!referralRewardInfo || !selectedProduct) return 0;

    const { rewardType, rewardValue } = referralRewardInfo;

    let discount = 0;
    if (rewardType === RewardType.PERCENTAGE_DISCOUNT) {
      discount = (baseAmount * rewardValue) / 100;
    } else if (rewardType === RewardType.FIXED_AMOUNT_DISCOUNT) {
      discount = rewardValue;
    }
    
    // İndirim tutarı base amount'tan fazla olamaz (negatif değer önleme)
    return Math.min(discount, baseAmount);
  };

  // Kullanıcı sayısı değişimi - kupon ve referans kodu kontrolü ile
  const handleUserCountChange = (value: number | null) => {
    if (value && value > 0) {
      // Kupon uygulanmışsa ve kişi sayısı değiştiriliyorsa uyarı ver
      if (appliedCoupon && appliedCoupon.lockedUserCount !== value) {
        showNotification('warning', 'Kupon İptal Edildi', 'Kişi sayısını değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.');
        setAppliedCoupon(null);
        setCouponCode('');
      }
      
      // Referans kodu uygulanmışsa ve kişi sayısı değiştiriliyorsa uyarı ver
      if (referralRewardInfo) {
        showNotification('info', 'Referans Kodu Güncellendi', 'Kişi sayısı değiştiği için referans kodu indirimi yeniden hesaplandı.');
      }
      
      setUserCount(value);
    }
  };

  // Paket seçimi - kupon ve referans kodu kontrolü ile
  const handleProductSelection = (product: Product) => {
    // Kupon uygulanmışsa ve paket değiştiriliyorsa uyarı ver
    if (appliedCoupon && appliedCoupon.lockedProductId !== product.id) {
      showNotification('warning', 'Kupon İptal Edildi', 'Paketi değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.');
      setAppliedCoupon(null);
      setCouponCode('');
    }
    
    // Referans kodu uygulanmışsa ve paket değiştiriliyorsa uyarı ver
    if (referralRewardInfo) {
      showNotification('info', 'Referans Kodu Güncellendi', 'Paket değiştiği için referans kodu indirimi yeniden hesaplandı.');
    }
    
    setSelectedPlan('paid');
    setSelectedProduct(product);
  };

  return (
    <Card className="form-card modern-card enhanced" loading={loading} style={{ marginTop: '0' }}>
      <div className="step-header" style={{ 
        background: 'white', 
        color: '#2d3748',
        padding: 'clamp(16px, 4vw, 24px) 0',
        marginBottom: 'clamp(16px, 3vw, 24px)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          fontSize: '24px',
          color: 'white'
        }}>
          <CreditCardOutlined />
        </div>
        <Title level={2} style={{ color: '#2d3748 !important', marginBottom: '8px' }}>
          Planınızı Seçin
        </Title>
        <Paragraph style={{ color: '#4a5568 !important', fontSize: '16px', marginBottom: '0' }}>
          İhtiyaçlarınıza uygun planı seçin ve hemen başlayın! 
          <span style={{ color: '#667eea', fontWeight: 600 }}> 14 gün ücretsiz deneme </span>
          ile risk almadan keşfedin.
        </Paragraph>
      </div>

      <Form
        form={form}
        onFinish={(values) => {
          // Temel fiyat hesaplama
          const baseAmount = selectedProduct 
            ? userCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth
            : 0;
          
          // Kupon indirimi
          const couponDiscount = appliedCoupon ? appliedCoupon.savings : 0;
          const amountAfterCoupon = appliedCoupon ? appliedCoupon.finalAmount : baseAmount;
          
          // Referans kodu indirimi (FREE_SUBSCRIPTION_DAYS hariç)
          const referralDiscount = referralRewardInfo && referralRewardInfo.rewardType !== RewardType.FREE_SUBSCRIPTION_DAYS
            ? calculateReferralDiscount(amountAfterCoupon)
            : 0;
          
          // Ara toplam (KDV öncesi, referans indirimi dahil) - minimum 0.1 TL
          const subtotal = Math.max(0.1, amountAfterCoupon - referralDiscount);
          
          // KDV hesaplama
          const tax = subtotal * 0.20;
          
          // Final amount (KDV dahil, referans indirimi dahil) - ekranda gösterilen toplam tutar ile aynı
          const finalAmount = subtotal + tax;
          
          // IYZICO dokümantasyonuna göre paket bilgilerini gönder
          const submitData = {
            ...values,
            selectedProduct,
            userCount,
            couponCode: appliedCoupon?.code || undefined,
            referralCode: referralRewardInfo ? referralCode : undefined,
            finalAmount: Math.round(finalAmount * 100) / 100, // Floating point precision hatası önleme
            billingAddress: billingAddress || {
              alias: "Varsayılan Adres",
              contactName: "Varsayılan İsim",
              address: "Varsayılan Adres",
              city: "İstanbul",
              country: "Turkey",
              zipCode: "34000",
              phone: "",
              email: ""
            }
          };
          onFinish(submitData);
        }}
        layout="vertical"
        size="large"
      >
        {/* Ana İçerik Kartı */}
        <Card className="pricing-main-card" style={{ padding: '16px', marginBottom: '24px' }}>
          <Title level={4} className="mb-4">
            <DollarOutlined className="me-2" />
            Plan Seçimi
          </Title>

          {/* Plan Seçimi - 14 Günlük Deneme + Premium Paketler */}
          {/* <Card className=" mb-4"> */}
            {/* <Title level={5} className="mb-3">
              <CalendarOutlined className="me-2" />
              Planınızı Seçin
            </Title> */}

            <Row gutter={[16, 16]} justify="center" style={{ marginBottom: '16px' }}>
              {/* 14 Günlük Deneme Kartı */}
              <Col xs={24} sm={12} md={6} lg={6}>
                <div 
                  className={`package-selection-card ${selectedPlan === 'trial' ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPlan('trial');
                    setSelectedProduct(null);
                  }}
                  style={{
                    border: selectedPlan === 'trial' ? '2px solid #11998e' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: selectedPlan === 'trial' ? '#f0fdfa' : 'white',
                    position: 'relative',
                    height: '100%'
                  }}
                >
                  {selectedPlan === 'trial' && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '8px',
                      background: '#11998e',
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      ✓
                    </div>
                  )}
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      fontSize: '24px',
                      color: 'white'
                    }}>
                      <GiftOutlined />
                    </div>
                    
                    <Title level={5} style={{ color: '#2d3748', marginBottom: '8px' }}>
                      14 Günlük Deneme
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                      Risk almadan deneyin
                      
                    </Text>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <Text style={{ fontSize: '14px', color: '#11998e', fontWeight: 600, display: 'block' }}>
                        50 kişilik kullanım hakkı
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Kredi kartı gerekmez
                      </Text>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Premium Paketler */}
              {products.filter(p => p.isActive).map((product) => (
                <Col xs={24} sm={8} lg={6} key={product.id}>
                  <div 
                    className={`package-selection-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                    onClick={() => handleProductSelection(product)}
                    style={{
                      border: selectedProduct?.id === product.id ? '2px solidrgb(205, 73, 246)' : '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: selectedProduct?.id === product.id ? 'rgb(245, 240, 254)' : 'white',
                      position: 'relative',
                      height: '100%'
                    }}
                  >
                    {/* İndirim yüzdesi sticker'ı - 3 aylık paket dışında */}
                    {product.durationMonths !== 3 && (() => {
                      const threeMonthProduct = products.find(p => p.durationMonths === 3);
                      if (threeMonthProduct) {
                        const threeMonthPrice = Number(threeMonthProduct.pricePerUserPerMonth);
                        const currentPrice = Number(product.pricePerUserPerMonth);
                        const discountPercentage = Math.round(((threeMonthPrice - currentPrice) / threeMonthPrice) * 100);
                        
                        if (discountPercentage > 0) {
                          return (
                            <div style={{
                              position: 'absolute',
                              top: '-24px',
                              right: '-12px',
                              background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                              color: 'white',
                              borderRadius: '50%',
                              width: '50px',
                              height: '50px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 10,
                              textAlign: 'center',
                              lineHeight: '1'
                            }}>
                              <div style={{ fontSize: '18px', marginBottom: '1px' }}>
                                %{discountPercentage}
                              </div>
                              <div style={{ fontSize: '10px' }}>
                                İndirim
                              </div>
                            </div>
                          );
                        }
                      }
                      return null;
                    })()}

                    {selectedProduct?.id === product.id && (
                      <div style={{
                        position: 'absolute',
                        top: '12px', // İndirim sticker'ı varsa aşağıya kaydır
                        left: '8px',
                        background: 'rgb(181, 44, 223)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        zIndex: 11
                      }}>
                        ✓
                      </div>
                    )}
                    
                    <div style={{ textAlign: 'center' }}>
                      <Title level={5} style={{ color: '#2d3748', marginBottom: '8px' }}>
                        {product.name}
                      </Title>
                      {/* <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                        {product.durationMonths} Aylık Paket
                      </Text> */}
                      
                      <div style={{ marginBottom: '12px' }}>
                        {/* 3 aylık paket dışında üzeri çizili fiyat göster */}
                        {product.durationMonths !== 3 ? (
                          <div style={{ marginBottom: '4px' }}>
                            <Text 
                              style={{ 
                                fontSize: '16px', 
                                fontWeight: 'normal', 
                                color: '#8c8c8c',
                                textDecoration: 'line-through'
                              }}
                            >
                              ₺{(Number(products.find(p => p.durationMonths === 3)?.pricePerUserPerMonth))}
                            </Text>
                            <br/>
                          </div>
                        ): (
                          <div>
                            <br/>
                          </div>
                        )}
                        
                        {/* Mevcut fiyat */}
                        <div>
                          <Text style={{ fontSize: '24px', fontWeight: 'bold', color: 'rgb(181, 44, 223)' }}>
                            ₺{product.pricePerUserPerMonth}
                          </Text>
                          <br/>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            /kullanıcı/ay
                          </Text>
                        </div>
                      </div>
                      
                      {product.description && (
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                          {product.description}
                        </Text>
                      )}
                      
                      {/* <div style={{ marginTop: '12px', padding: '8px', background: '#fff7e6', borderRadius: '8px' }}>
                        <Text style={{ fontSize: '12px', color: '#faad14', fontWeight: 600 }}>
                          Toplam: ₺{Math.round(newUserCount * product.durationMonths * product.pricePerUserPerMonth)}
                        </Text>
                        <br />
                        <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                          ({newUserCount} kullanıcı × {product.durationMonths} ay)
                        </Text>
                      </div> */}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          {/* </Card> */}

          {/* Kullanıcı Sayısı Hesaplayıcı */}
          {selectedPlan !== 'trial' && (
          <Card className="mb-4" size="small" style={{ padding: '16px', border: '1px solid #e2e8f0' }}>
            <Row gutter={[24, 16]} align="middle">
              <Col xs={24}>
                <div className="calculator-section">
                  <Title level={5} className="calculator-title">
                    <TeamOutlined className="me-2" />
                    Kaç kullanıcınız olacak?
                  </Title>
                  <InputNumber
                    min={1}
                    max={500000}
                    value={userCount}
                    onChange={handleUserCountChange}
                    size="large"
                    style={{ width: '100%' }}
                    parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                    addonAfter="kullanıcı"
                  />
                  <Text type="secondary" className="pricing-info mt-2">
                    {selectedProduct && (
                      <>
                        Her kullanıcı: <strong>₺{selectedProduct.pricePerUserPerMonth}/ay</strong>
                        {appliedCoupon && (
                          <Text type="success" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                            ✨ Kişi sayısı değiştirilirse "{appliedCoupon.code}" kupon kodunuz devre dışı olacak. Kuponu tekrar kullanabilirsiniz.
                          </Text>
                        )}
                      </>
                    )}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
          )}

          {/* Referans Kodu Bölümü - Premium için - Sadece uygulanmış kod varsa göster */}
          {selectedPlan === 'paid' && selectedProduct && referralRewardInfo && (
            <Card className="referral-section-card" style={{ border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <Title level={5} className="mb-3">
                <GiftOutlined className="me-2" />
                Referans Kodu Uygulandı
              </Title>

              <Alert
                message={
                  <Space>
                    <GiftOutlined />
                    <span>Referans Kodu: <strong>{referralCode}</strong></span>
                    <Badge 
                      color="purple"
                      text={
                        referralRewardInfo.rewardType === RewardType.PERCENTAGE_DISCOUNT
                          ? `%${referralRewardInfo.rewardValue} İndirim`
                          : referralRewardInfo.rewardType === RewardType.FIXED_AMOUNT_DISCOUNT
                          ? `₺${referralRewardInfo.rewardValue} İndirim`
                          : `${referralRewardInfo.rewardValue} Gün Ücretsiz`
                      }
                    />
                  </Space>
                }
                description={
                  <div>
                    <Text>{referralRewardInfo.description}</Text>
                    {referralRewardInfo.rewardType !== RewardType.FREE_SUBSCRIPTION_DAYS && (
                      <>
                        <br />
                        <Text style={{ color: '#7338a1', fontWeight: 600 }}>
                          <strong>İndirim: ₺{calculateReferralDiscount(userCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth).toFixed(2)}</strong>
                        </Text>
                      </>
                    )}
                  </div>
                }
                type="success"
                showIcon
                closable
                onClose={handleRemoveReferralCode}
                style={{
                  borderRadius: '12px',
                  border: '2px solid #7338a1'
                }}
              />
          </Card>
          )}

          {/* Kupon Kodu Bölümü - Premium için */}
          {selectedPlan === 'paid' && selectedProduct && (
            <Card className="coupon-section-card" style={{ border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <Title level={5} className="mb-3">
                <TagOutlined className="me-2" />
                Kupon Kodunuz Var mı?
              </Title>
              
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24}>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      placeholder="Kupon kodunu girin"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      size="large"
                      disabled={!!appliedCoupon}
                      style={{
                        borderRadius: '12px 0 0 12px',
                        border: '2px solid #e2e8f0',
                        fontSize: '16px',
                        height: '48px'
                      }}
                    />
                    <Button
                      type="primary"
                      onClick={handleApplyCoupon}
                      loading={couponLoading}
                      disabled={!!appliedCoupon}
                      size="large"
                      style={{
                        height: '48px',
                        borderRadius: '0 12px 12px 0',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        fontWeight: 600
                      }}
                    >
                      Uygula
                    </Button>
                  </Space.Compact>
                </Col>
              </Row>

              {appliedCoupon && (
                <Alert
                  message={
                    <Space>
                      <PercentageOutlined />
                      <span>Kupon Uygulandı: <strong>{appliedCoupon.code}</strong></span>
                      {appliedCoupon.couponType && (
                        <Badge 
                          color={appliedCoupon.couponType === 'percentage' ? 'green' : 'blue'}
                          text={appliedCoupon.couponType === 'percentage' 
                            ? `%${appliedCoupon.couponValue} İndirim`
                            : `₺${appliedCoupon.couponValue} İndirim`
                          }
                        />
                      )}
                    </Space>
                  }
                  description={
                    <div>
                      <Text>{appliedCoupon.message}</Text>
                      <br />
                      <Text style={{ color: '#11998e', fontWeight: 600 }}>
                        <strong>Tasarruf: ₺{appliedCoupon.savings.toFixed(2)}</strong>
                      </Text>
                    </div>
                  }
                  type="success"
                  showIcon
                  closable
                  onClose={handleRemoveCoupon}
                  style={{
                    marginTop: '16px',
                    borderRadius: '12px',
                    border: '2px solid #11998e'
                  }}
                />
              )}
            </Card>
          )}

          {/* Seçilen Paket Bilgileri */}
          {/* {selectedPlan === 'paid' && selectedProduct && (() => {
            const pricePerUser = Number(selectedProduct.pricePerUserPerMonth);
            
            const monthlyBasisPrice = userCount * pricePerUser;
            const totalMonthlyEquivalent = monthlyBasisPrice * selectedProduct.durationMonths;
            
            const packagePrice = userCount * selectedProduct.durationMonths * pricePerUser;
            
            const savings = totalMonthlyEquivalent - packagePrice;
            const discountPercentage = savings > 0 ? ((savings / totalMonthlyEquivalent) * 100) : 0;
            
            return (
              <Card style={{ padding: '16px', marginBottom: '16px', border: '2px solid #52c41a', background: '#f6ffed' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <div className="selected-package-info">
                      <Title level={5} style={{ color: '#52c41a', marginBottom: 8 }}>
                        <TrophyOutlined className="me-2" />
                        Seçilen Paket: {selectedProduct.durationMonths} Aylık Plan
                      </Title>
                      <div className="package-details">
                        <Text type="secondary">
                          <strong>{userCount} kullanıcı</strong> × <strong>{selectedProduct.durationMonths} ay</strong> × <strong>₺{pricePerUser.toFixed(2)}/kullanıcı</strong>
                        </Text>
                        <br />
                        <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                          Paket Fiyatı: ₺{packagePrice.toFixed(2)}
                        </Text>
                        {savings > 0 && (
                          <>
                            <br />
                            <Text type="secondary" style={{ textDecoration: 'line-through' }}>
                              Aylık ödemeler toplamı: ₺{totalMonthlyEquivalent.toFixed(2)}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="package-benefits">
                      <Title level={5} style={{ color: '#faad14', marginBottom: 8 }}>
                        <GiftOutlined className="me-2" />
                        Paket Avantajları
                      </Title>
                      <div className="benefits-list">
                        {savings > 0 && (
                          <Text type="success" style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 'bold' }}>
                            <CheckCircleOutlined className="me-1" />
                            %{discountPercentage.toFixed(1)} indirim - ₺{savings.toFixed(2)} tasarruf
                          </Text>
                        )}
                        <Text type="success" style={{ display: 'block', marginBottom: 4 }}>
                          <CheckCircleOutlined className="me-1" />
                          {savings > 0 
                            ? `Aylık ödemelere göre %${discountPercentage.toFixed(1)} indirimli tek ödeme`
                            : `Aylık ₺${monthlyBasisPrice.toFixed(2)} yerine tek ödeme`
                          }
                        </Text>
                        <Text type="success" style={{ display: 'block', marginBottom: 4 }}>
                          <CheckCircleOutlined className="me-1" />
                          {selectedProduct.durationMonths} ay kesintisiz hizmet garantisi
                        </Text>
                        <Text type="success" style={{ display: 'block', marginBottom: 4 }}>
                          <CheckCircleOutlined className="me-1" />
                          Ödeme güvenliği ve kolaylığı
                        </Text>
                        {selectedProduct.durationMonths >= 12 && (
                          <Text type="success" style={{ display: 'block' }}>
                            <CheckCircleOutlined className="me-1" />
                            Uzun vadeli plan avantajı
                          </Text>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            );
          })()} */}

          {/* Billing Address Form - Premium için */}
          {selectedPlan === 'paid' && selectedProduct && setBillingAddress && (
            <BillingAddressForm
              form={null}
              companyData={companyData}
              onBillingAddressChange={setBillingAddress}
              onValidationChange={setIsBillingAddressValid}
            />
          )}

          {/* Fiyat Özeti */}
          {selectedPlan === 'paid' && selectedProduct && (
            <Card className="price-breakdown-card mb-4" style={{ padding: '16px' }}>
              <Title level={5} className="mb-3">
                <CalculatorOutlined className="me-2" />
                Fiyat Detayları
              </Title>
              
              <div className="price-breakdown-details">
                {(() => {
                  // Temel fiyat hesaplama
                  const baseAmount = userCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
                  
                  // Kupon indirimi (eğer varsa)
                  const couponDiscount = appliedCoupon ? appliedCoupon.savings : 0;
                  const amountAfterCoupon = appliedCoupon ? appliedCoupon.finalAmount : baseAmount;
                  
                  // Referans kodu indirimi (eğer varsa ve FREE_SUBSCRIPTION_DAYS değilse)
                  const referralDiscount = referralRewardInfo && referralRewardInfo.rewardType !== RewardType.FREE_SUBSCRIPTION_DAYS
                    ? calculateReferralDiscount(amountAfterCoupon)
                    : 0;
                  
                  // Kupon sonrası fiyattan referans indirimi çıkar
                  const amountAfterReferral = amountAfterCoupon - referralDiscount;
                  
                  // Ara toplam (KDV öncesi) - minimum 0.1 TL
                  const subtotal = Math.max(0.1, amountAfterReferral);
                  
                  // KDV hesaplama
                  const tax = subtotal * 0.20;
                  
                  // Toplam tutar
                  const total = subtotal + tax;
                  
                  return (
                    <>
                <Row justify="space-between" className="price-line">
                  <Col><Text>Paket Ücreti ({userCount} kullanıcı × {selectedProduct.durationMonths} ay)</Text></Col>
                        <Col><Text>₺{baseAmount.toFixed(2)}</Text></Col>
                </Row>

                {appliedCoupon && (
                  <Row justify="space-between" className="price-line discount">
                    <Col><Text type="success">Kupon İndirimi ({appliedCoupon.code})</Text></Col>
                          <Col><Text type="success">-₺{couponDiscount.toFixed(2)}</Text></Col>
                        </Row>
                      )}

                      {referralRewardInfo && referralRewardInfo.rewardType !== RewardType.FREE_SUBSCRIPTION_DAYS && (
                        <Row justify="space-between" className="price-line discount">
                          <Col>
                            <Text type="success">
                              Referans Kodu İndirimi ({referralCode})
                              {referralRewardInfo.rewardType === RewardType.PERCENTAGE_DISCOUNT && (
                                <Text type="secondary" style={{ fontSize: '12px', marginLeft: '4px' }}>
                                  (%{referralRewardInfo.rewardValue})
                                </Text>
                              )}
                            </Text>
                          </Col>
                          <Col><Text type="success">-₺{referralDiscount.toFixed(2)}</Text></Col>
                        </Row>
                      )}

                      {referralRewardInfo && referralRewardInfo.rewardType === RewardType.FREE_SUBSCRIPTION_DAYS && (
                        <Row justify="space-between" className="price-line">
                          <Col>
                            <Text type="success">
                              <GiftOutlined /> Referans Kodu Bonusu ({referralCode})
                            </Text>
                          </Col>
                          <Col>
                            <Text type="success" strong>
                              +{referralRewardInfo.rewardValue} Gün Ücretsiz
                            </Text>
                          </Col>
                  </Row>
                )}

                <Divider style={{ margin: '12px 0' }} />

                <Row justify="space-between" className="price-line subtotal">
                  <Col><Text strong>Ara Toplam</Text></Col>
                        <Col><Text strong>₺{subtotal.toFixed(2)}</Text></Col>
                </Row>

                <Row justify="space-between" className="price-line tax">
                  <Col>
                    <Text>
                      KDV (%20)
                      <Tooltip title="Katma Değer Vergisi yasal olarak eklenir">
                        <InfoCircleOutlined className="ms-1" />
                      </Tooltip>
                    </Text>
                  </Col>
                        <Col><Text>₺{tax.toFixed(2)}</Text></Col>
                </Row>

                <Divider style={{ margin: '12px 0' }} />

                <Row justify="space-between" className="price-line total">
                  <Col><Title level={5} style={{ margin: 0 }}>Toplam Tutar</Title></Col>
                  <Col>
                    <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                            ₺{total.toFixed(2)}
                      <Text type="secondary" className="ms-1">
                        ({selectedProduct.durationMonths} ay)
                      </Text>
                    </Title>
                  </Col>
                </Row>
                    </>
                  );
                })()}
              </div>
            </Card>
          )}

          {/* Güvenli Ödeme Bilgilendirmesi */}
          {selectedPlan === 'paid' && selectedProduct && (
            <div className="purchase-section">
              <Alert
                message="Güvenli Ödeme Garantisi"
                description="Bir sonraki adımda güvenli ödeme sayfasına yönlendirileceksiniz. Kart bilgileriniz 256-bit SSL şifreleme ile korunur."
                type="info"
                showIcon
                className="mb-4"
              />
            </div>
          )}
        </Card>

        <div className="form-footer" style={{ marginTop: 'clamp(24px, 4vw, 32px)', paddingTop: 'clamp(16px, 3vw, 24px)', borderTop: '1px solid #e5e7eb' }}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12}>
              <Button 
                type="default" 
                size="large" 
                onClick={onPrevious} 
                block 
                style={{ 
                  height: 'clamp(48px, 8vw, 56px)',
                  borderRadius: 'clamp(12px, 2vw, 16px)',
                  border: '2px solid #e2e8f0',
                  color: '#374151',
                  backgroundColor: '#ffffff',
                  fontSize: 'clamp(14px, 2.5vw, 16px)',
                  fontWeight: 600
                }}
              >
                Önceki Adım
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                htmlType="submit"
                icon={!loading && (selectedPlan === 'paid' ? <CreditCardOutlined /> : <RocketOutlined />)}
                loading={loading}
                size="large"
                block
                disabled={
                  loading ||
                  (selectedPlan === 'paid' && (!selectedProduct || userCount <= 0 || !isBillingAddressValid))
                }
                style={{
                  height: 'clamp(48px, 8vw, 56px)',
                  fontSize: 'clamp(14px, 2.5vw, 16px)',
                  fontWeight: 600,
                  borderRadius: 'clamp(12px, 2vw, 16px)',
                  background: selectedPlan === 'paid' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  border: 'none',
                  boxShadow: selectedPlan === 'paid' 
                    ? '0 8px 24px rgba(102, 126, 234, 0.4)' 
                    : '0 8px 24px rgba(17, 153, 142, 0.4)',
                  color: 'white'
                }}
              >
                {loading 
                  ? (selectedPlan === 'paid' ? 'Ödeme Sayfası Hazırlanıyor...' : 'Hesap Hazırlanıyor...')
                  : (selectedPlan === 'paid' && selectedProduct
                    ? (() => {
                        // Toplam tutarı hesapla (referans kodu dahil)
                        const baseAmount = userCount * selectedProduct.durationMonths * selectedProduct.pricePerUserPerMonth;
                        const amountAfterCoupon = appliedCoupon ? appliedCoupon.finalAmount : baseAmount;
                        const referralDiscount = referralRewardInfo && referralRewardInfo.rewardType !== RewardType.FREE_SUBSCRIPTION_DAYS
                          ? calculateReferralDiscount(amountAfterCoupon)
                          : 0;
                        const subtotal = Math.max(0.1, amountAfterCoupon - referralDiscount);
                        const total = subtotal * 1.20;
                        return `₺${total.toFixed(2)} - Güvenli Ödeme Sayfasına Git`;
                      })()
                    : selectedPlan === 'paid' && !selectedProduct
                    ? 'Lütfen Paket Seçin'
                    : selectedPlan === 'paid' && !billingAddress
                    ? 'Lütfen Fatura Adresi Belirleyin'
                    : 'Ücretsiz Denemeyi Başlat'
                  )
                }
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </Card>
  );
};

export default PricingForm;